import React from 'react'
import type { RatingPolarity } from '@digital-twins/shared-types'

import { PlotBox } from '../components/chart-segment/PlotBox'
import { ChartSegmentSelectionScope, TotalSampleTag } from '../components/chart-segment/selection-scope'
import { ChartSegmentTitleSlot } from '../components/chart-segment/ChartSegmentTitleSlot'
import { chartSegmentGearInsetTop } from '../components/chart-segment/gear-layout'
import {
  CHART_SEGMENT_TABS_BOTTOM_PX,
  chartSegmentTabsFigureTop,
} from '../components/chart-segment/ChartSegmentTabs'
import type { ChartSegmentFigureResult } from '../components/chart-segment/ChartSegmentTabs'
import { TextClamp } from '../components/text/TextClamp'
import { clampArray } from '../foundations/slide-limits'
import { buildScaleLegendResult } from '../foundations/scale-legend'
import { schemaBackedProseClampProps } from '../foundations/text-overflow-policy'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { SlideFootnote } from '../layouts/SlideFootnote'
import { SlideFrame } from '../layouts/SlideFrame'
import { SlideHeaderChrome } from '../layouts/SlideHeaderChrome'
import {
  HeatGridMatrix,
  MATRIX_RATING_CHROME,
  MATRIX_ROW_LABEL_TYPE,
  matrixCellHeight as heatGridMatrixCellHeight,
  matrixRowGroupStarts,
  matrixRowLabelMaxLines,
  matrixRowsBudget,
  matrixRowsBudgetAfterGroups,
} from '../plots/heat-grid/HeatGridMatrix'
import { HeatGridPlotData, heatGridDrawnView, heatGridStatFormat } from '../plots/heat-grid/HeatGridPlot'
import { formatHeatValue } from '../plots/heat-grid/format'
import { matrixRatingSlideAdapter, readStampedHeatGrid } from '../plots/heat-grid/readAdapters'
import { heatFill } from '../plots/heat-grid/style'
import { EditableSlideText } from './EditableSlideText'
import { registerSlide } from './registry'
import { presetSlot } from '../layouts/presetSlots'
import { SlotBullets, SlotHeadline, SlotNarrative } from '../slots'
import { PLOT_CARD_SURFACE } from '../slots/PlotCard'
import type { EditableSlideProps } from './contracts'
import type { MatrixRatingSlideData } from './renderer-internal-types'
import type { ChartSegmentInnerView } from '@digital-twins/shared-types'
import type { HeatGridRow } from '@digital-twins/slide-schemas/generated/plot-types'

// The grid's geometry lives with the heat-grid plot (`HeatGridMatrix`); these
// names stay importable from the slide for its callers and tests.
export { matrixRowGroupStarts, matrixRowLabelMaxLines, matrixRowsBudget, matrixRowsBudgetAfterGroups }

type MatrixMeasure = MatrixRatingSlideData['measures'][number]
type MatrixRow = MatrixRatingSlideData['rows'][number]

const MATRIX_RATING_SLIDE_TYPE = 'matrix-rating' as const

/**
 * The chart title IS the question, whole (the builder no longer cuts it). The schema gives
 * it three 14px lines (`x-limits.text.title`), which hold about 200 characters at this panel
 * width. The third line is paid for out of the row stack below (see `MATRIX_RATING_CHROME`),
 * never out of the scale legend at the foot of the panel.
 */
const MATRIX_RATING_TITLE_CLAMP = schemaBackedProseClampProps('title', MATRIX_RATING_SLIDE_TYPE)

// The rail-wide binding (plots plan AG): the plot panel and the text rail from
// `packages/slide-schemas/layouts/` (rail-wide.json plus this preset's overrides).
const PLOT_RECT = presetSlot(MATRIX_RATING_SLIDE_TYPE, 'plot').rect!
const RAIL_RECT = presetSlot(MATRIX_RATING_SLIDE_TYPE, 'rail').rect!
const HEADLINE = presetSlot(MATRIX_RATING_SLIDE_TYPE, 'headline').text!
const NARRATIVE = presetSlot(MATRIX_RATING_SLIDE_TYPE, 'narrative').text!
const BULLETS = presetSlot(MATRIX_RATING_SLIDE_TYPE, 'bullets').text!
const MATRIX_RATING_PANEL_TOP = PLOT_RECT.y
const MATRIX_RATING_PANEL_HEIGHT = PLOT_RECT.h

const FIGMA_GRAY = 'var(--slide-text-muted)'


function formatScaleValue(value: number) {
  if (Number.isInteger(value)) {
    return String(value)
  }

  return value.toFixed(1)
}

function narrativeFromPayload(props: MatrixRatingSlideData) {
  const explicitSubtitle = (props.actionSubtitle || '').replace(/\s+/g, ' ').trim()
  if (explicitSubtitle) {
    return explicitSubtitle
  }

  return ''
}

function isScoreGuideFooter(text: string): boolean {
  return /\bscore guide\b/i.test(text)
}

function renderInlineEmphasis(text: string) {
  const parts = text.split('**')
  if (parts.length === 1) {
    return text
  }

  return parts.map((part, index) => (
    index % 2 === 1 ? (
      <span key={`${part}-${index}`} style={{ fontWeight: 600 }}>
        {part}
      </span>
    ) : (
      part
    )
  ))
}


/** English templates for a deck stored before its builder emitted these labels. */
// legacy reader only: a deck built since AI part 3 stamps `scaleRange` and `lowerIsBetter` (`deck_labels.mixed_scale_labels`).
const LEGACY_SCALE_RANGE = '{min} to {max}'
const LEGACY_LOWER_IS_BETTER = '(lower is better)'

/**
 * One measure's own bounds on a mixed-scale matrix ("Effort: 1 to 5 (lower is
 * better)"), from the deck language's display labels: the range is a worded
 * template, never a dash.
 */
function mixedScaleLegendEntry(
  measure: MatrixRatingSlideData['measures'][number],
  displayLabels: MatrixRatingSlideData['displayLabels'],
): string {
  const range = (displayLabels?.scaleRange || LEGACY_SCALE_RANGE)
    .replace('{min}', String(measure.min))
    .replace('{max}', String(measure.max))
  const lowerIsBetter =
    measure.polarity === 'lower_is_better' ? ` ${displayLabels?.lowerIsBetter || LEGACY_LOWER_IS_BETTER}` : ''
  return `${measure.label}: ${range}${lowerIsBetter}`
}

/**
 * The swatch strip labels one shared colour scale, so it may only borrow the
 * slide-level anchors when those speak for every measure on the grid. That is
 * exactly the case where the legend line below collapses to a single unprefixed
 * segment, so the strip reuses the legend's own determination rather than
 * recomputing it. Otherwise the strip would show measure 0's wording spanning
 * the shared scale while the legend states the scales differ.
 *
 * The non-collapsed path restores the pre-branch precedence: the generic
 * display labels first (both matrix builders always emit them), then the
 * slide-level anchors, then the raw bounds.
 */
/**
 * The grid's polarity as the swatch strip needs it: every measure
 * lower-is-better (the strip keeps the scale's words, ends swapped), or a mix
 * or a middle-is-best measure, whose heat is its closeness to the middle (no
 * shared words: "Lower/Higher performance").
 */
function scalePolarity(measures: ReadonlyArray<{ polarity?: RatingPolarity | null }>) {
  const polarities = measures.map((measure) => measure.polarity ?? 'higher_is_better')
  const lower = polarities.filter((polarity) => polarity === 'lower_is_better').length
  return {
    everyLowerIsBetter: measures.length > 0 && lower === measures.length,
    mixedPolarity: new Set(polarities).size > 1 || polarities.includes('middle_is_best'),
  }
}

function MatrixScaleSwatches(props: {
  scale: MatrixRatingSlideData['scale']
  displayLabels?: MatrixRatingSlideData['displayLabels']
  anchorsCoverEveryMeasure?: boolean
  /**
   * The ends of a stat view's heat (Top N: 0% to 100%; NPS: -100 to +100), which
   * replace the rating scale's ends when the grid draws that view.
   */
  viewEnds?: { low: string; high: string } | null
  /**
   * Every measure is lower-is-better, so the mean heat is folded toward the
   * favourable end (`keyReversed`): the light swatch is the scale's top and
   * the dark swatch its bottom. The strip keeps the scale's own words and
   * swaps their ends (L18: "Lower/Higher performance" on a pain scale).
   */
  reversed?: boolean
}) {
  const { scale, displayLabels, anchorsCoverEveryMeasure, viewEnds, reversed } = props
  // legacy reader only: a deck built since AI part 3 stamps `lowerPerformance` / `higherPerformance`.
  const scaleLow = viewEnds ? viewEnds.low : !scale ? displayLabels?.lowerPerformance ?? 'Lower performance' : anchorsCoverEveryMeasure && scale.minLabel
    ? scale.minLabel
    : displayLabels?.scaleLow || scale.minLabel || formatScaleValue(scale.min)
  // legacy reader only: see `scaleLow`.
  const scaleHigh = viewEnds ? viewEnds.high : !scale ? displayLabels?.higherPerformance ?? 'Higher performance' : anchorsCoverEveryMeasure && scale.maxLabel
    ? scale.maxLabel
    : displayLabels?.scaleHigh || scale.maxLabel || formatScaleValue(scale.max)
  // Only the mean heat is folded; a stat view's ends are its own.
  const swap = Boolean(reversed && scale && !viewEnds)
  const lowLabel = swap ? scaleHigh : scaleLow
  const highLabel = swap ? scaleLow : scaleHigh

  return (
    <div
      data-testid="matrix-rating-scale"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minWidth: 0,
      }}
    >
      <TextClamp
        minLines={1}
        maxLines={1}
        lineHeight="1.2"
        style={{
          fontSize: 10,
          color: FIGMA_GRAY,
          minWidth: 8,
        }}
      >
        {lowLabel}
      </TextClamp>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((value, idx) => (
          <div
            key={idx}
            style={{
              width: 18,
              height: 12,
              borderRadius: 2,
              background: heatFill(value),
            }}
          />
        ))}
      </div>
      <TextClamp
        minLines={1}
        maxLines={1}
        lineHeight="1.2"
        style={{
          fontSize: 10,
          color: FIGMA_GRAY,
          minWidth: 8,
          textAlign: 'right',
        }}
      >
        {highLabel}
      </TextClamp>
    </div>
  )
}

/**
 * A measure's own numeric bounds, falling back to the slide-level scale.
 *
 * Units are first-class per measure, and the slide-level scale is derived from
 * the first measure's unit alone, so on a mixed-unit matrix it names the wrong
 * numbers for every other column. Payloads built before measures carried their
 * own bounds fall back to the old slide-level behaviour.
 */
function measureBound(value: number | null | undefined, fallback: number) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  return fallback
}


/** The baked panel's own budget: the panel inside its padding, minus the chrome above. */
export const MATRIX_RATING_BAKED_ROWS_BUDGET = matrixRowsBudget({
  columnHeight:
    MATRIX_RATING_PANEL_HEIGHT - MATRIX_RATING_CHROME.padTop - MATRIX_RATING_CHROME.padBottom,
  hasTitle: true,
})

/** Cell height for a row count, floored against the budget (the baked panel's by default). */
export function matrixCellHeight(
  rowCount: number,
  rowsBudgetPx: number = MATRIX_RATING_BAKED_ROWS_BUDGET,
) {
  return heatGridMatrixCellHeight(rowCount, rowsBudgetPx)
}

function MatrixRatingHeatmap(props: {
  chartTitle?: string | null
  /** The measures' bounds and anchors, for the scale legend. */
  measures: MatrixMeasure[]
  /** The grid drawn: the stamped heat-grid plot, else the stored slide through its adapter. */
  grid: HeatGridPlotData
  /** The edit path of a row label (`rows.N.label`, or the plot's when one is stamped). */
  rowLabelField?: (index: number) => string
  scale: MatrixRatingSlideData['scale']
  page?: MatrixRatingSlideData['page']
  displayLabels?: MatrixRatingSlideData['displayLabels']
  editable?: boolean
  onEdit?: (field: string, value: string) => void
  /**
   * D17: threaded down from the slide so the clamps below can drop their Radix trigger
   * in a static export, where no pointer or focus ever arrives and a tooltip would only
   * advertise text the reader cannot reach.
   */
  renderMode?: 'interactive' | 'static'
  /**
   * Pixels the row stack may occupy. Defaults to the baked panel's own budget; a
   * breakdown passes the band the tabs chrome leaves it, so both draw the same chart.
   */
  rowsBudgetPx?: number
  /**
   * Whether this instance draws the title row. A breakdown renders inside the tabs
   * chrome, which already prints the title above the segment pills, so drawing it
   * again here would print the question twice and spend a band doing it.
   */
  showTitleBand?: boolean
}) {
  const {
    chartTitle,
    measures,
    grid,
    rowLabelField,
    scale,
    page,
    displayLabels,
    editable,
    onEdit,
    renderMode,
    rowsBudgetPx = MATRIX_RATING_BAKED_ROWS_BUDGET,
    showTitleBand = true,
  } = props
  const { everyLowerIsBetter, mixedPolarity } = scalePolarity(measures)
  // A static render draws the scale legend whole (wave D); the height it takes past its
  // declared two lines comes out of the row stack's budget, so the grid still fits.
  const legendRef = React.useRef<HTMLElement | null>(null)
  const [legendExtraPx, setLegendExtraPx] = React.useState(0)
  React.useLayoutEffect(() => {
    if (renderMode !== 'static' || !legendRef.current) return
    const drawn = legendRef.current.getBoundingClientRect().height
    // The declared band is `MATRIX_RATING_CHROME.legend`: a 12px margin and two lines.
    const extra = Math.max(0, drawn + 12 - MATRIX_RATING_CHROME.legend)
    setLegendExtraPx((prev) => (prev === extra ? prev : extra))
  })
  const legend = buildScaleLegendResult(
    measures.map((measure) => ({
      measureLabel: measure.label,
      min: measureBound(measure.min, scale?.min ?? 0),
      max: measureBound(measure.max, scale?.max ?? 1),
      minLabel: measure.minLabel,
      maxLabel: measure.maxLabel,
    })),
  )
  // The stat view Python stamped as the grid's default (decision 20, AG-2); Average
  // when the grid was built without views.
  const view = heatGridDrawnView(grid, grid.defaultView)
  const viewFormat = heatGridStatFormat(view, grid)
  const viewEnds =
    view === 'mean'
      ? null
      : {
          low: formatHeatValue(view === 'nps' ? -100 : 0, { ...viewFormat, decimals: 0 }) ?? '',
          high: formatHeatValue(100, { ...viewFormat, decimals: 0 }) ?? '',
        }
  // A stat view's legend names the view as Python filled it ("Top 2", "Top 2 / Top 3",
  // "NPS"); the rating scale's legend is Average's: each measure's worded range on a
  // mixed-scale grid, the shared scale's legend otherwise.
  const scaleLegendText =
    view !== 'mean'
      ? grid.statLabels?.[view]
      : !scale
        ? measures.map((measure) => mixedScaleLegendEntry(measure, displayLabels)).join(' · ')
        : legend.text
  const renderRowLabel = (row: HeatGridRow, index: number, maxLines: number, chartLabel: { hintIndex: number }) => (
    <EditableSlideText
      data-testid="matrix-rating-row-label"
      value={row.label}
      field={rowLabelField ? rowLabelField(index) : `rows.${index}.label`}
      editable={editable}
      onEdit={onEdit}
      tooltipOnTruncate
      chartLabel={{ ...chartLabel, hintSize: 10 }}
      renderMode={renderMode}
      minLines={1}
      maxLines={maxLines}
      lineHeight={`${MATRIX_ROW_LABEL_TYPE.lineHeight}`}
      style={{
        fontSize: MATRIX_ROW_LABEL_TYPE.fontSize,
        fontWeight: 500,
        color: 'var(--slide-text-primary)',
        minWidth: 0,
      }}
    />
  )

  return (
    <div
      data-testid="matrix-rating-heatmap"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        width: '100%',
        height: '100%',
      }}
    >
      {showTitleBand ? (
      <ChartSegmentTitleSlot
        testId="matrix-rating-overlay-header"
        contentPadRight={32}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          minWidth: 0,
          // The band is a declared height in the budget above, and it never yields it.
          // As a shrinkable flex item in this fixed-height column it was squashed to a
          // single line the moment the rows oversubscribed the panel, and `-webkit-line-
          // clamp` only ellipsises PAST its last line — so the question was cut mid-word
          // with no cue that anything was missing.
          minHeight: MATRIX_RATING_CHROME.titleBand,
          flexShrink: 0,
        }}
      >
        <EditableSlideText
          data-testid="matrix-rating-chart-title"
          value={chartTitle || ''}
          field="title"
          editable={editable}
          onEdit={onEdit}
          title={chartTitle || ''}
          minLines={1}
          maxLines={MATRIX_RATING_TITLE_CLAMP.maxLines}
          lineHeight="1.2"
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--slide-text-primary)',
          }}
        />
        {page ? (
          <TextClamp
            data-testid="matrix-rating-page-cue"
            // legacy reader only: the builder stamps `page.label`.
            title={page.label || `Page ${page.current} of ${page.total}`}
            minLines={1}
            maxLines={1}
            lineHeight="1.2"
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: FIGMA_GRAY,
              textAlign: 'right',
              minWidth: 86,
            }}
          >
            {/* legacy reader only: the builder stamps `page.label`. */}
            {page.label || `Page ${page.current} / ${page.total}`}
          </TextClamp>
        ) : null}
        <MatrixScaleSwatches
          scale={mixedPolarity ? null : scale}
          reversed={everyLowerIsBetter}
          displayLabels={displayLabels}
          anchorsCoverEveryMeasure={legend.collapsed}
          viewEnds={viewEnds}
        />
      </ChartSegmentTitleSlot>
      ) : null}

      {/*
       * Layout re-budget (D4): the title above can take 3 lines. The right panel is a
       * fixed 872x674 frame (rail-wide, AG) with overflow:hidden and no scroll, so that growth is paid
       * for out of the row stack (see `matrixCellHeight`), never out of this gap or the
       * scale legend at the foot. The grid itself is the heat-grid plot at this
       * slide's geometry (plots plan Q): Python's figures, heat and marks.
       */}
      <HeatGridMatrix
        data={grid}
        rowsBudgetPx={rowsBudgetPx - legendExtraPx}
        headerMarginTop={showTitleBand ? MATRIX_RATING_CHROME.titleGap : 0}
        renderMode={renderMode}
        renderRowLabel={renderRowLabel}
      />

      {scaleLegendText ? (
        <TextClamp
          innerRef={legendRef}
          data-testid="matrix-rating-scale-legend"
          title={scaleLegendText}
          minLines={1}
          maxLines={2}
          lineHeight="1.4"
          style={{
            marginTop: 12,
            fontSize: 11,
            color: FIGMA_GRAY,
            minWidth: 0,
            // What 1 and 5 mean is not optional chrome: without it every number on the
            // grid is unreadable. It keeps its two lines whatever the rows want.
            flexShrink: 0,
          }}
        >
          {scaleLegendText}
        </TextClamp>
      ) : null}
    </div>
  )
}

/**
 * Height the tabs chrome leaves its figure inside this panel. A function of the bands
 * above the figure, never of how many segments the breakdown returned — the chart must
 * not resize because a cut produced one more pill.
 */
const MATRIX_RATING_SEGMENT_FIGURE_HEIGHT =
  MATRIX_RATING_PANEL_HEIGHT -
  chartSegmentTabsFigureTop(true, chartSegmentGearInsetTop(MATRIX_RATING_PANEL_TOP)) -
  CHART_SEGMENT_TABS_BOTTOM_PX

/**
 * Rows budget for a cut, so a segment's cells are sized the way the baked chart's are.
 *
 * Capped at the baked budget rather than taken as-is: the tabs chrome is a little
 * shorter than this slide's own title band, so the honest arithmetic gives a cut MORE
 * room than the un-cut chart, and its cells would draw a few pixels taller. The reader
 * is comparing a segment against the chart they just left, so the two draw at the same
 * size and the leftover stays empty.
 */
const MATRIX_RATING_SEGMENT_ROWS_BUDGET = Math.min(
  MATRIX_RATING_BAKED_ROWS_BUDGET,
  matrixRowsBudget({
    columnHeight: MATRIX_RATING_SEGMENT_FIGURE_HEIGHT - MATRIX_RATING_CHROME.padBottom,
    hasTitle: false,
  }),
)

/**
 * A cut of this slide, drawn with the slide's own chart.
 *
 * Without this the breakdown fell through to the shared inner view — a 120px label
 * column, 28px stripes, unclamped labels, no average column and no scale legend. The
 * reader clicked a segment and got what read as a different slide. The generic renderer
 * stays as the fallback for any kind this one does not draw.
 *
 * A row average is the producer's, never recomputed here: the producer leaves it null
 * when the measures are different constructs, and a mean invented at render time would
 * put back the figure it refused.
 */
function renderMatrixRatingSegmentFigure(
  view: ChartSegmentInnerView,
  params: {
    scale: MatrixRatingSlideData['scale']
    displayLabels?: MatrixRatingSlideData['displayLabels']
    renderMode?: 'interactive' | 'static'
    plot?: unknown
  },
): ChartSegmentFigureResult | null {
  if (view.kind !== 'matrix-rating') {
    return null
  }
  const measures: Array<MatrixMeasure> = view.measures.map((measure) => ({
    id: measure.id,
    label: measure.label,
    // The drilldown view carries no metric or cell identity; neither is read below the
    // PlotBox, which resolves its request from the baked payload's own columns.
    metricId: measure.id,
    unit: measure.unit || '',
    polarity: measure.polarity,
    min: measure.min,
    max: measure.max,
  }))
  // The tab's heat-grid (Python's figures, heat and one strongest cell per
  // measure); a tab computed before the plot was stamped reads its view.
  const stamped = readStampedHeatGrid(params.plot)
  const rows: Array<MatrixRow> = view.rows.map((row) => ({
    id: row.id,
    label: row.label,
    average: typeof row.average === 'number' ? row.average : null,
    cells: row.cells.map((cell) => ({
      measureId: cell.measureId,
      value: cell.value,
      normalizedValue: cell.normalizedValue,
    })),
  }))
  const grid = stamped
    ? stamped.data
    : matrixRatingSlideAdapter.read({ type: 'matrix-rating', measures, rows, scale: view.scale ?? params.scale }).data

  return {
    node: (
      <div
        style={{
          height: '100%',
          minHeight: 0,
          paddingBottom: MATRIX_RATING_CHROME.padBottom,
          paddingRight: 16,
          boxSizing: 'border-box',
        }}
      >
        <MatrixRatingHeatmap
          measures={measures}
          grid={grid}
          scale={view.scale ?? params.scale}
          displayLabels={params.displayLabels}
          renderMode={params.renderMode}
          showTitleBand={false}
          rowsBudgetPx={MATRIX_RATING_SEGMENT_ROWS_BUDGET}
        />
      </div>
    ),
  }
}

function MatrixRatingSlideComponent(props: MatrixRatingSlideData & EditableSlideProps) {
  const {
    eyebrow,
    actionTitle,
    footerText,
    clientLogoUrl,
    scale,
    editable,
    onEdit,
    // D17: forwarded to the heatmap, which owns the clamps that offer truncation
    // tooltips. A static export cannot open one, so they fall back to the native `title`.
    renderMode,
  } = props

  const resolvedLogoUrl = clientLogoUrl ?? null
  const resolvedActionTitle = sanitizeNarrativeText(actionTitle)
  const measures = clampArray(props.measures || [], 'matrix-rating', 'measures')
  const { everyLowerIsBetter, mixedPolarity } = scalePolarity(measures)
  const rows = clampArray(props.rows || [], 'matrix-rating', 'rows')
  const narrativeBody = narrativeFromPayload(props)
  const normalizedFooterText = (footerText || '').replace(/\s+/g, ' ').trim()
  const showFooterAsLegend = isScoreGuideFooter(normalizedFooterText)
  // legacy reader only: the builder stamps `displayLabels.phase` and the narrative eyebrow.
  const phaseLabel = props.measurementPhase
    ? props.displayLabels?.phase ||
      (props.measurementPhase === 'pre' ? 'Before exposure' : 'After exposure')
    : ''
  const eyebrowText = eyebrow || 'Mean rating · Concepts × Measures'
  const scaleAnchorsCoverEveryMeasure = buildScaleLegendResult(
    measures.map((measure) => ({
      measureLabel: measure.label,
      min: measureBound(measure.min, scale?.min ?? 0),
      max: measureBound(measure.max, scale?.max ?? 1),
      minLabel: measure.minLabel,
      maxLabel: measure.maxLabel,
    })),
  ).collapsed
  const renderSegmentFigure = React.useCallback(
    (view: ChartSegmentInnerView, _displayLabels?: Record<string, string>, plot?: unknown) =>
      renderMatrixRatingSegmentFigure(view, {
        scale,
        displayLabels: props.displayLabels,
        renderMode,
        plot,
      }),
    [scale, props.displayLabels, renderMode],
  )
  // The grid: the heat-grid plot Python stamped, else the stored slide's fields
  // through the read adapter (a deck built before the plot existed).
  const hasStampedPlot = readStampedHeatGrid(props.plot) !== null
  const read = matrixRatingSlideAdapter.read({ ...props, measures, rows }).data
  // A deck stored before its builder emitted `displayLabels` printed the
  // renderer's own "Avg." heading; a stored slide keeps it (legacy only).
  const grid = hasStampedPlot || read.labels.avg_column
    ? read
    : { ...read, labels: { ...read.labels, avg_column: 'Avg.' } }
  const rowLabelField = (index: number) => (hasStampedPlot ? `plot.data.rows.${index}.label` : `rows.${index}.label`)
  // An edited row label writes the plot's row and the stored row alike, so a
  // reader of either (an export, a deck rebuilt without the plot) keeps the edit.
  const onGridEdit = onEdit
    ? (field: string, value: string) => {
        onEdit(field, value)
        const plotRow = /^plot\.data\.rows\.(\d+)\.label$/.exec(field)
        if (plotRow) {
          onEdit(`rows.${plotRow[1]}.label`, value)
        }
      }
    : undefined

  // A signed column (a paired grid's change) is a movement over tested assets. The
  // segment view recomputes each column as a rating over the question's own rows and
  // has no reader for it, so the grid offers no breakdown rather than drawing wrong
  // figures under a segment's name.
  const isSegmentable = !measures.some((measure) => measure.valueFormat === 'signed')
  const heatmapPanel = (
    <>
          {/* Foot padding, not 0: the scale legend used to sit flush on the panel's
              bottom edge, where the panel's own `overflow: hidden` clipped its second
              line mid-glyph. Budgeted in MATRIX_RATING_CHROME.padBottom. */}
          <div
            style={{
              height: '100%',
              padding: `${MATRIX_RATING_CHROME.padTop}px 32px ${MATRIX_RATING_CHROME.padBottom}px`,
              boxSizing: 'border-box',
              minWidth: 0,
              minHeight: 0,
            }}
          >
          <MatrixRatingHeatmap
            chartTitle={props.title}
            measures={measures}
            grid={grid}
            rowLabelField={rowLabelField}
            scale={scale}
            page={props.page}
            displayLabels={props.displayLabels}
            editable={editable}
            onEdit={onGridEdit}
            renderMode={renderMode}
          />
          </div>
    </>
  )

  return (
    <ChartSegmentSelectionScope>
    <SlideFrame fullBleed background="var(--ds-slide-bg)">
      <div
        data-testid="matrix-rating-layout"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--ds-slide-bg)',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
          <SlideHeaderChrome
            eyebrowText={eyebrowText}
            eyebrowSuffix={phaseLabel ? (
              <span data-testid="matrix-rating-phase">{phaseLabel}</span>
            ) : undefined}
            clientLogoUrl={resolvedLogoUrl}
            eyebrowTextTransform="none"
          />
        </div>

        <div
          data-testid="matrix-rating-left-column"
          style={{
            position: 'absolute',
            left: RAIL_RECT.x,
            top: RAIL_RECT.y,
            width: RAIL_RECT.w,
            display: 'flex',
            flexDirection: 'column',
            gap: 36,
            minWidth: 0,
          }}
        >
          <SlotHeadline text={resolvedActionTitle || ''} maxLines={HEADLINE.maxLines} minFontSize={HEADLINE.minFontSize} width={HEADLINE.width} editable={editable} onEdit={onEdit} />

          {narrativeBody ? (
            <SlotNarrative
              value={narrativeBody}
              clamp={{ minLines: 1, maxLines: NARRATIVE.maxLines, lineHeight: '1.5' }}
              color={FIGMA_GRAY}
              testId="matrix-rating-narrative"
              style={{ width: NARRATIVE.width }}
              shrink
              renderMode={renderMode}
            >
              {renderInlineEmphasis(narrativeBody)}
            </SlotNarrative>
          ) : null}
          {props.insightBullets?.length ? (
            <SlotBullets
              bullets={props.insightBullets}
              fontSize={BULLETS.fontSize}
              gap={8}
              maxLines={BULLETS.maxLines}
              dotColor="var(--slide-accent)"
              color={FIGMA_GRAY}
              testId="matrix-rating-insight-bullets"
              // The list is the text box plus the 8px dot and its 12px gap.
              style={{ width: BULLETS.width + 20 }}
              editable={editable}
              onEdit={onEdit}
              renderMode={renderMode}
            />
          ) : null}
          {showFooterAsLegend ? (
            <TextClamp
              data-testid="matrix-rating-score-guide"
              minLines={1}
              maxLines={1}
              lineHeight="1.4"
              style={{
                marginTop: -12,
                fontSize: 12,
                color: 'var(--ds-text-faint)',
                letterSpacing: '0.01em',
                width: RAIL_RECT.w,
              }}
            >
              {normalizedFooterText}
            </TextClamp>
          ) : null}
          <TotalSampleTag style={{ position: 'absolute', bottom: 0, right: 0 }} />
        </div>

        <div
          data-testid="matrix-rating-right-panel"
          style={{
            position: 'absolute',
            left: PLOT_RECT.x,
            top: MATRIX_RATING_PANEL_TOP,
            width: PLOT_RECT.w,
            height: MATRIX_RATING_PANEL_HEIGHT,
            ...PLOT_CARD_SURFACE,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {isSegmentable ? (
          <PlotBox
            slide={props as unknown as Record<string, unknown>}
            slideType="matrix-rating"
            columns={measures.map((measure) => ({
              id: measure.id,
              label: measure.label,
              metricId: measure.metricId,
              cellId: measure.cellId,
              unit: measure.unit,
              min: measure.min,
              max: measure.max,
            }))}
            rows={rows.map((row) => ({ id: row.id, label: row.label }))}
            title={props.title}
            gearInsetTop={chartSegmentGearInsetTop(MATRIX_RATING_PANEL_TOP)}
            renderSegmentFigure={renderSegmentFigure}
            // The heat scale's key. It lives in this slide's own title row on the baked
            // chart; inside the tabs chrome the title row belongs to the tabs, so the
            // key is handed over rather than dropped — a heatmap with no key is a grid
            // of coloured boxes.
            segmentLegend={
              <MatrixScaleSwatches
                scale={mixedPolarity ? null : scale}
                reversed={everyLowerIsBetter}
                displayLabels={props.displayLabels}
                // Same determination the baked strip makes, so the two never disagree
                // about whether the slide-level anchors speak for every measure.
                anchorsCoverEveryMeasure={scaleAnchorsCoverEveryMeasure}
              />
            }
          >
            {heatmapPanel}
          </PlotBox>
          ) : heatmapPanel}
        </div>

        {showFooterAsLegend ? null : <SlideFootnote text={normalizedFooterText} />}
      </div>
    </SlideFrame>
    </ChartSegmentSelectionScope>
  )
}

export const MatrixRatingSlide = registerSlide(
  'matrix-rating',
  MatrixRatingSlideComponent,
)
